/* eslint-disable react-refresh/only-export-components -- i18n module re-exports */
/**
 * i18n entry point.
 *
 * Public API:
 *   useT()         — React hook, returns the typed dictionary for the current market
 *   getT(market)   — Pure function, returns the typed dictionary for a given market
 *   MarketProvider — React context provider, wraps App to make useT() work
 *   useMarket()    — React hook, returns the resolved Market value
 *
 * Usage in a component:
 *   const t = useT();
 *   return <h1>{t.landing_title}</h1>;
 *
 * Usage in a domain function:
 *   const t = getT('intl');
 *   return t.export_footer;
 */

import { createContext, type ReactNode, use, useMemo } from "react";
import type { Market } from "../domain/location/types";
import { en } from "./en";
import { sv } from "./sv";
import type { Dictionary } from "./types";

export type { FormatPriceOptions } from "./currency";
export { convertCurrency, formatPrice, getCurrencyCode } from "./currency";
export type { Dictionary } from "./types";

/* ── Dictionary registry ───────────────────────────────── */

const DICTIONARIES: Record<Market, Dictionary> = {
	se: sv,
	intl: en,
};

/* ── Pure function (domain / utils layer) ──────────────── */

/**
 * Get the dictionary for a specific market.
 * Safe to call outside React — no hooks, no context.
 */
export function getT(market: Market): Dictionary {
	return DICTIONARIES[market];
}

/* ── React context ─────────────────────────────────────── */

const MarketContext = createContext<Market>("se");

interface MarketProviderProps {
	market: Market;
	children: ReactNode;
}

/**
 * Wrap your application (or a subtree) with MarketProvider to set the
 * active market for all `useT()` consumers below.
 */
export function MarketProvider({ market, children }: MarketProviderProps) {
	return (
		<MarketContext.Provider value={market}>{children}</MarketContext.Provider>
	);
}

/* ── React hooks ───────────────────────────────────────── */

/**
 * Returns the resolved Market value from context.
 */
export function useMarket(): Market {
	return use(MarketContext);
}

/**
 * Returns the typed dictionary for the current market context.
 * Must be called within a <MarketProvider>.
 */
export function useT(): Dictionary {
	const market = use(MarketContext);
	return useMemo(() => getT(market), [market]);
}
